import { FirebaseApp, getApp, initializeApp } from 'firebase/app';
import {
  Auth,
  AuthCredential,
  confirmPasswordReset,
  connectAuthEmulator,
  getAuth,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  updatePassword,
  User
} from 'firebase/auth';
import {
  collection,
  CollectionReference,
  connectFirestoreEmulator,
  Firestore,
  getDocs,
  getFirestore,
  orderBy,
  query,
  QueryConstraint,
  where
} from 'firebase/firestore';
import { connectFunctionsEmulator, Functions, getFunctions } from 'firebase/functions';
import {
  connectStorageEmulator,
  FirebaseStorage,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  UploadResult
} from 'firebase/storage';
import ENV_VARIABLES from '../config';
import { generateFileName } from '../Utils/HelperFunctions/helperFunctions';
import { CustomDocumentDataType, DocumentDataType } from '../Utils/types';

class Firebase {
  private firebaseConfig = {
    apiKey: ENV_VARIABLES.apiKey,
    authDomain: ENV_VARIABLES.authDomain,
    projectId: ENV_VARIABLES.projectId,
    storageBucket: ENV_VARIABLES.storageBucket,
    messagingSenderId: ENV_VARIABLES.messagingSenderId,
    appId: ENV_VARIABLES.appId
  };
  public app: FirebaseApp;
  public storage: FirebaseStorage;
  public auth: Auth;
  public firestore: Firestore;
  public functions: Functions;
  //public appCheck: AppCheck;

  public mediaCollectionRef: CollectionReference<DocumentDataType>;

  updateMediaCollectionRef = (organisationId: string | undefined) => {
    if (!organisationId) {
      console.warn('⚠️ updateMediaCollectionRef: organisationId is undefined, using default "UNDEFINED"');
      organisationId = 'UNDEFINED';
    }
    this.mediaCollectionRef = collection(
      this.firestore,
      'organisations',
      organisationId,
      'media'
    ) as CollectionReference<DocumentDataType>;
  };

  constructor() {
    // Validate required Firebase config values
    const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
    const missingFields = requiredFields.filter(
      (field) => !this.firebaseConfig[field as keyof typeof this.firebaseConfig]
    );

    if (missingFields.length > 0) {
      const mode = import.meta.env.MODE || 'unknown';
      const envPrefix = mode === 'production' ? 'VITE_FIREBASE_' : 'VITE_STAGING_FIREBASE_';

      // Map field names to environment variable names
      const fieldToEnvVar = (field: string): string => {
        const mapping: Record<string, string> = {
          apiKey: 'API_KEY',
          authDomain: 'AUTH_DOMAIN',
          projectId: 'PROJECT_ID',
          storageBucket: 'STORAGE_BUCKET',
          messagingSenderId: 'MESSAGING_SENDER_ID',
          appId: 'APP_ID'
        };
        return mapping[field] || field.toUpperCase();
      };

      console.error(
        `❌ Firebase configuration error: Missing required fields: ${missingFields.join(', ')}\n` +
          `   Current mode: ${mode}\n` +
          `   Please set the following environment variables:\n` +
          `   ${missingFields.map((field) => `   - ${envPrefix}${fieldToEnvVar(field)}`).join('\n')}\n` +
          `   Or use development mode: yarn start:dev`
      );
      throw new Error(`Firebase configuration is incomplete. Missing: ${missingFields.join(', ')}`);
    }

    this.app = initializeApp(this.firebaseConfig);

    this.storage = getStorage(this.app);
    this.auth = getAuth();
    this.firestore = getFirestore();
    this.functions = getFunctions(getApp());

    // Local dev environment: point the client SDKs at the Firebase emulator
    // suite instead of real cloud services. Opt-in via VITE_USE_EMULATORS so
    // this never accidentally runs against staging/prod config.
    if (import.meta.env.VITE_USE_EMULATORS === 'true') {
      connectStorageEmulator(this.storage, 'localhost', 9199);
      connectAuthEmulator(this.auth, 'http://localhost:9099');
      connectFirestoreEmulator(this.firestore, 'localhost', 8080);
      connectFunctionsEmulator(this.functions, 'localhost', 5001);
    }
    // Pass your reCAPTCHA v3 site key (public key) to activate(). Make sure this
    // key is the counterpart to the secret key you set in the Firebase console.
    /*     this.appCheck = initializeAppCheck(this.app, {
      provider: new ReCaptchaV3Provider(ENV_VARIABLES.reCAPTCHASiteKey!),
      // Optional argument. If true, the SDK automatically refreshes App Check
      // tokens as needed.
      isTokenAutoRefreshEnabled: true
    }); */

    // TODO: change so we do not have a default ref?
    this.mediaCollectionRef = collection(this.firestore, 'UNDEFINED') as CollectionReference<DocumentDataType>;
  }

  // TODO: Consider removing fully or moving fetch code from GlobalState here
  // fetchAllData = async (): Promise<CustomDocumentDataType[]> => {
  //   const q = query(this.mediaCollectionRef, orderBy('timestamp', 'desc'));
  //   const querySnapshot = await getDocs(q);
  //   const result: CustomDocumentDataType[] = querySnapshot.docs.map((doc) => ({
  //     docId: doc.id,
  //     docRef: doc.ref,
  //     ...doc.data()
  //   }));
  //   return result;
  // };

  fetchAllDataByDate = async (
    start: Date | number | string | null,
    end: Date | number | string | null
  ): Promise<CustomDocumentDataType[]> => {
    const constraints: QueryConstraint[] = [];
    if (start) constraints.push(where('timestamp', '>=', start));
    if (end) constraints.push(where('timestamp', '<=', end));
    constraints.push(orderBy('timestamp', 'desc'));
    const q = query(this.mediaCollectionRef, ...constraints);
    const querySnapshot = await getDocs(q);
    const result: CustomDocumentDataType[] = querySnapshot.docs.map((doc) => ({
      docId: doc.id,
      docRef: doc.ref,
      ...doc.data()
    }));
    return result;
  };

  // A media card's thumbnail (OQ-183); like downloadFile, it logs nothing: the URL carries the token.
  fileUrl = (fileName: string, folder: string): Promise<string> =>
    getDownloadURL(ref(this.storage, `gs://${ENV_VARIABLES.storageBucket}/${folder}/${fileName}`));

  downloadFile = async (fileName: string, folder: string, extension?: string): Promise<void> => {
    // Nothing about the file is logged: its URL carries the access token (OQ-166).
    const downloadUrl = await getDownloadURL(
      ref(this.storage, `gs://${ENV_VARIABLES.storageBucket}/${folder}/${fileName}`)
    );

    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.onload = () => {
      const blob = xhr.response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateFileName(fileName, extension);
      document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
      a.click();
      a.remove();
    };
    xhr.open('GET', downloadUrl);
    xhr.send();
  };

  uploadFile = async (file: File, folder: string): Promise<UploadResult> => {
    const storageRef = ref(this.storage, `${folder}/${file.name}`);
    return await uploadBytes(storageRef, file);
  };

  // createDocument = async (data: DocumentDataType): Promise<DocumentReference<DocumentDataType>> => {
  //   return await addDoc(this.mediaCollectionRef, data);
  // };

  signInWithEmailAndPassword = async (email: string, password: string) => {
    return signInWithEmailAndPassword(this.auth, email, password);
  };

  confirmPasswordReset = async (oobCode: string, password: string) => {
    return confirmPasswordReset(this.auth, oobCode, password);
  };

  updatePassword = async (user: User, newPassword: string) => {
    return updatePassword(user, newPassword);
  };

  reauthenticateWithCredential = async (user: User, credential: AuthCredential) => {
    return reauthenticateWithCredential(user, credential);
  };
}

export const FirebaseService = new Firebase();
